import { useQueryClient } from '@tanstack/react-query';
import { getMissingApprovals } from 'lib/api/approvals';
import { APPROVAL_TYPES, COMITEE_TYPES } from 'lib/api/approvals/types';
import { getRecieverFactoringRequestOverview } from 'lib/api/factoring';
import SharedApprovalPage from 'pages/mobile/Finance/shared/ApprovalPage';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

interface ApprovalLocationState {
  type: 'approve' | 'reject';
  quoteId?: number;
  userRoles?: Array<{ id: number; name: string }>;
}

const FactoringApproval: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const state = location.state as ApprovalLocationState | undefined;
  const { data: overview } = getRecieverFactoringRequestOverview(dispatch, {
    requestId: id ?? '',
  });
  const quoteIdFromOverview = overview?.quoteId ?? null;
  const quoteId = state?.quoteId ?? quoteIdFromOverview ?? 0;

  const { data: missingApprovals } = getMissingApprovals(
    dispatch,
    APPROVAL_TYPES.FACTORING_QUOTE,
    quoteId,
    COMITEE_TYPES.RISK,
  );

  const userRolesFromState = state?.userRoles;
  const userRolesFromApi = useMemo(() => {
    if (!missingApprovals?.roles) return [];
    return missingApprovals.roles
      .filter((r) => r.userCanApprove)
      .map((r) => ({ id: r.roleId, name: r.roleName }));
  }, [missingApprovals?.roles]);
  const userRoles = userRolesFromState ?? userRolesFromApi;

  const isError = !id || (overview != null && quoteId <= 0);

  const onSuccess = () => {
    if (id) {
      void queryClient.invalidateQueries({
        queryKey: ['factoring', 'request', id, 'overview'],
      });
    }
    navigate(-1);
  };

  if (overview === undefined) {
    return null;
  }

  return (
    <SharedApprovalPage
      approvalType={APPROVAL_TYPES.FACTORING_QUOTE}
      entityId={quoteId}
      requestId={id ?? ''}
      userRoles={userRoles}
      onSuccess={onSuccess}
      isError={isError}
    />
  );
};

export default FactoringApproval;
